"use client";

import Image from "next/image";
import { useState } from "react";

import { cx } from "@/utils/cx";

import {
	getTokenColor,
	getTokenIconPath,
	hasTokenIcon,
	normalizeTokenDisplaySymbol,
} from "../utils/tokenIcons";

interface TokenIconProps {
	symbol: string;
	size?: "sm" | "md" | "lg";
	className?: string;
	showSymbol?: boolean;
}

const sizeMap = {
	sm: { px: 20, className: "size-5", text: "text-[10px]" },
	md: { px: 24, className: "size-6", text: "text-xs" },
	lg: { px: 32, className: "size-8", text: "text-sm" },
};

function FallbackBadge({
	symbol,
	sizeClass,
	textSize,
}: {
	symbol: string;
	sizeClass: string;
	textSize: string;
}) {
	return (
		<div
			className={cx(
				"flex shrink-0 items-center justify-center rounded-full font-semibold text-white",
				sizeClass,
			)}
			style={{ backgroundColor: getTokenColor(symbol) }}
		>
			<span className={textSize}>{symbol.charAt(0).toUpperCase()}</span>
		</div>
	);
}

export function TokenIcon({ symbol, size = "md", className, showSymbol = true }: TokenIconProps) {
	const [imgError, setImgError] = useState(false);
	const { px, className: sizeClass, text: textSize } = sizeMap[size];
	const displaySymbol = normalizeTokenDisplaySymbol(symbol);
	const iconPath = hasTokenIcon(displaySymbol) ? getTokenIconPath(displaySymbol) : null;

	return (
		<div className={cx("flex items-center gap-2", className)}>
			{iconPath && !imgError ? (
				<Image
					src={iconPath}
					alt={displaySymbol}
					width={px}
					height={px}
					className={cx("size-full object-contain", sizeClass)}
					onError={() => setImgError(true)}
				/>
			) : (
				<FallbackBadge symbol={displaySymbol} sizeClass={sizeClass} textSize={textSize} />
			)}
			{showSymbol && <span>{displaySymbol}</span>}
		</div>
	);
}

// Simpler version for just the icon without text
export function TokenIconOnly({
	symbol,
	size = "md",
	className,
}: Omit<TokenIconProps, "showSymbol">) {
	return <TokenIcon symbol={symbol} size={size} className={className} showSymbol={false} />;
}
